<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                <v-icon class="me-2">mdi-filter</v-icon>
                Bonus Process Report Filter
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="handleFilterSubmit">
                  <v-row class="py-2" dense>
                    <v-col cols="12" md="3">
                      <v-select
                        v-model="filters.year"
                        :items="yearOptions"
                        label="Year"
                        variant="outlined"
                        density="compact"
                        clearable
                        hide-details="auto"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-select
                        v-model="filters.month"
                        :items="months"
                        item-title="label"
                        item-value="value"
                        label="Month"
                        variant="outlined"
                        density="compact"
                        clearable
                        hide-details="auto"
                      />
                    </v-col>
                    <v-col cols="12" md="2" class="d-flex align-end ga-2">
                      <v-btn
                        type="submit"
                        color="primary"
                        :loading="loading"
                        prepend-icon="mdi-magnify"
                        class="pl-2"
                        density="compact"
                        height="40"
                        variant="flat"
                      >
                        Search
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <v-row class="mt-2">
        <v-col cols="12">
          <v-expansion-panels v-model="listPanel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                <v-icon class="me-2">mdi-chart-box</v-icon>
                Bonus Process Report
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    />
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToCSV"
                        >
                          CSV
                        </v-btn>
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="primary"
                          @click="exportToExcel"
                        >
                          EXCEL
                        </v-btn>
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                        >
                          PDF
                        </v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="tableHeaders"
                  :items="reportData"
                  :loading="loading"
                  :search="search"
                  class="mt-4"
                  density="compact"
                  hover
                  :items-per-page="itemsPerPage"
                  v-model:items-per-page="itemsPerPage"
                >
                  <template #item.index="{ index }">
                    {{ index + 1 }}
                  </template>
                  <template #loading>
                    <div class="py-6 text-center text-medium-emphasis">
                      Loading bonus process report...
                    </div>
                  </template>
                  <template #item.employee_code="{ item }">
                    <span class="font-weight-medium">{{ item.employee?.code || '—' }}</span>
                  </template>
                  <template #item.employee_name="{ item }">
                    {{ item.employee?.name || '—' }}
                  </template>
                  <template #item.bonus_amount="{ item }">
                    <div class="text-end">{{ formatNumber(item.bonus_amount) }}</div>
                  </template>
                  <template #item.payable_amount="{ item }">
                    <div class="text-end"><span class="font-weight-medium">{{ formatNumber(item.payable_amount) }}</span></div>
                  </template>
                  <template #item.payment_status="{ item }">
                    <v-chip
                      :color="getPaymentStatusColor(item.payment_status)"
                      size="small"
                      class="text-capitalize"
                    >
                      {{ formatPaymentStatus(item.payment_status) }}
                    </v-chip>
                  </template>
                  <template #body.append v-if="reportData && reportData.length > 0">
                    <tr class="v-data-table__tr" style="font-weight: bold; background-color: #f5f5f5;">
                      <td class="v-data-table__td text-center">—</td>
                      <td colspan="2" class="v-data-table__td">
                        <strong>Total:</strong>
                      </td>
                      <td class="v-data-table__td text-end">
                        <div class="text-end"><strong>{{ formatNumber(totals.bonus_amount) }}</strong></div>
                      </td>
                      <td class="v-data-table__td text-end">
                        <div class="text-end"><strong>{{ formatNumber(totals.payable_amount) }}</strong></div>
                      </td>
                      <td class="v-data-table__td"></td>
                    </tr>
                  </template>
                  <template #no-data>
                    <div class="py-6 text-center text-medium-emphasis">
                      No bonus process data found. Try adjusting filters or run a bonus process.
                    </div>
                  </template>
                </v-data-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, reactive, computed, getCurrentInstance } from 'vue'
import { toast } from 'vue3-toastify'
import { useStore } from 'vuex'

const instance = getCurrentInstance()
const appContext = instance?.appContext
const store = useStore()
const panel = ref([0])
const listPanel = ref([0])
const loading = ref(false)
const itemsPerPage = ref(10)
const search = ref('')
const reportData = ref([])

// Options
const months = [
  { label: 'January', value: 1 },
  { label: 'February', value: 2 },
  { label: 'March', value: 3 },
  { label: 'April', value: 4 },
  { label: 'May', value: 5 },
  { label: 'June', value: 6 },
  { label: 'July', value: 7 },
  { label: 'August', value: 8 },
  { label: 'September', value: 9 },
  { label: 'October', value: 10 },
  { label: 'November', value: 11 },
  { label: 'December', value: 12 }
]

// Generate year options (from 2020 to current year + 5)
const currentYear = new Date().getFullYear()
const yearOptions = computed(() => {
  const years = []
  for (let year = 2020; year <= currentYear + 5; year++) {
    years.push(year)
  }
  return years.reverse() // Show most recent years first
})

// Data
const filters = reactive({
  year: null,
  month: null
})

const tableHeaders = [
  { title: '#SL', key: 'index', sortable: false, width: '60px', align: 'center' },
  { title: 'Employee Code', key: 'employee_code', sortable: false },
  { title: 'Employee Name', key: 'employee_name', sortable: false },
  { title: 'Bonus Amount', key: 'bonus_amount', sortable: false, align: 'end' },
  { title: 'Payable Amount', key: 'payable_amount', sortable: false, align: 'end' },
  { title: 'Payment Status', key: 'payment_status', sortable: false, align: 'center', width: '130px' }
]

// Computed
const totals = computed(() => {
  if (!reportData.value || reportData.value.length === 0) {
    return {
      bonus_amount: 0,
      payable_amount: 0
    }
  }

  return reportData.value.reduce((acc, item) => {
    acc.bonus_amount += parseFloat(item.bonus_amount || 0)
    acc.payable_amount += parseFloat(item.payable_amount || 0)
    return acc
  }, {
    bonus_amount: 0,
    payable_amount: 0
  })
})

// Methods
const fetchBonusProcessReport = async () => {
  loading.value = true
  try {
    const sanitizedFilters = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== null && value !== '')
    )
    const response = await store.dispatch('getBonusProcessReport', sanitizedFilters)

    if (response.data.success) {
      // Flatten nested properties for search
      reportData.value = (response.data.data || []).map(item => {
        const status = item.payment_status || ''
        return {
          ...item,
          employee_code: item.employee?.code || '',
          employee_name: item.employee?.name || '',
          payment_status_formatted: status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : 'Pending'
        }
      })
    } else {
      reportData.value = []
      toast.error(response.data?.message || 'Failed to load bonus process report')
    }
  } catch (error) {
    reportData.value = []
    console.error('Error fetching bonus process report:', error)
    
    // Check if it's a Vuex unknown action error
    if (error.message && error.message.includes('unknown action type')) {
      toast.error('Store module not loaded. Please restart the dev server.')
    } else {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load bonus process report'
      toast.error(errorMessage)
    }
  } finally {
    loading.value = false
  }
}

const handleFilterSubmit = () => {
  fetchBonusProcessReport()
}

const formatNumber = (value, fraction = 2) => {
  if (value === undefined || value === null || value === '' || isNaN(value)) return '0.00'
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction
  })
}

const formatPaymentStatus = (status) => {
  if (!status) return 'Pending'
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
}

const getPaymentStatusColor = (status) => {
  if (!status) return 'orange'
  switch (status.toLowerCase()) {
    case 'pending':
      return 'orange'
    case 'paid':
      return 'success'
    case 'partial':
      return 'info'
    default:
      return 'grey'
  }
}

// Computed for company info
const getCurrentCompany = computed(() => {
  return store.getters.getCurrentCompany || {}
})

const getFileServerUrl = computed(() => {
  return store.getters.getFileServerUrl || ''
})

// Format number for export
// Export functions
const exportToCSV = () => {
  if (!reportData.value || reportData.value.length === 0) {
    toast.warning('No data to export')
    return
  }

  const data = [
    ['Bonus Process Report'],
    [`Year: ${filters.year || 'All'}`],
    [`Month: ${filters.month ? months.find(m => m.value === filters.month)?.label : 'All'}`]
  ]
  
  data.push([], ['# SL', 'Employee Code', 'Employee Name', 'Bonus Amount', 'Payable Amount', 'Payment Status'], [])

  reportData.value.forEach((item, index) => {
    data.push([
      index + 1,
      item.employee?.code || '—',
      item.employee?.name || '—',
      formatNumber(item.bonus_amount),
      formatNumber(item.payable_amount),
      formatPaymentStatus(item.payment_status)
    ])
  })

  if (reportData.value.length > 0) {
    data.push([])
    data.push([
      '', '', 'Total:',
      formatNumber(totals.value.bonus_amount),
      formatNumber(totals.value.payable_amount),
      ''
    ])
  }

  const proxy = appContext?.config?.globalProperties
  if (proxy?.$exportTableToCSV && proxy?.$fileDate) {
    proxy.$exportTableToCSV(data, 'bonus_process_report_' + proxy.$fileDate())
  }
}

const exportToExcel = () => {
  if (!reportData.value || reportData.value.length === 0) {
    toast.warning('No data to export')
    return
  }

  const data = [
    ['Bonus Process Report'],
    [`Year: ${filters.year || 'All'}`],
    [`Month: ${filters.month ? months.find(m => m.value === filters.month)?.label : 'All'}`]
  ]
  
  data.push([], ['# SL', 'Employee Code', 'Employee Name', 'Bonus Amount', 'Payable Amount', 'Payment Status'], [])

  reportData.value.forEach((item, index) => {
    data.push([
      index + 1,
      item.employee?.code || '—',
      item.employee?.name || '—',
      formatNumber(item.bonus_amount),
      formatNumber(item.payable_amount),
      formatPaymentStatus(item.payment_status)
    ])
  })

  if (reportData.value.length > 0) {
    data.push([])
    data.push([
      '', '', 'Total:',
      formatNumber(totals.value.bonus_amount),
      formatNumber(totals.value.payable_amount),
      ''
    ])
  }

  const proxy = appContext?.config?.globalProperties
  if (proxy?.$exportTableToExcel && proxy?.$fileDate) {
    proxy.$exportTableToExcel(data, 'bonus_process_report_' + proxy.$fileDate())
  }
}

const exportToPDF = async () => {
  if (!reportData.value || reportData.value.length === 0) {
    toast.warning('No data to export')
    return
  }

  const company = getCurrentCompany.value
  const fileServerUrl = getFileServerUrl.value

  let printWindow = window.open('', 'PRINT', `width=${screen.width}, height=${screen.height}, left=0, top=0`)
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <meta http-equiv="X-UA-Compatible" content="ie=edge">
        <title>Bonus Process Report</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Arimo:ital,wght@0,400..700;1,400..700&display=swap" rel="stylesheet">
        <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.8/dist/css/bootstrap.min.css" rel="stylesheet">
        <link href="https://cdn.jsdelivr.net/npm/@mdi/font@7.4.47/css/materialdesignicons.min.css" rel="stylesheet">
        <style>
          body { font-family: 'Arimo', sans-serif; margin: 0px; }
          .invoice-header { display:flex; justify-content:space-between; align-items:center; border-bottom:2px solid #eee; padding-bottom:10px; }
          .invoice-header img { max-height: 70px; max-width: 150px; }
          .table th, .table td { border: 1px solid #000; padding: 8px; vertical-align: middle; font-size: 12px; }
          .table th { background-color: #f0f0f0; color: #333; }
          .text-end { text-align: right; }
          .text-center { text-align: center; }
          h4 { font-size: 20px; color: #333; }
          p { font-size: 14px; color: #555; margin: 2px 0; }
          strong { font-weight: bold; color: #333; }
          @page { margin: 0.5in; size: letter; }
          .logo-container { display:flex; align-items:center; }
          .logo-container img { margin-left: 10px; }
          .table { width: 100%; border-collapse: collapse; table-layout: auto; }
          thead { display: table-header-group; }
          .totals-row { page-break-inside: avoid; }
          .totals-row td { font-weight: bold; background-color: #f5f5f5 !important; }
        </style>
      </head>
      <body>
        <div class="container-fluid position-relative">
          <div class="invoice-header">
            <div>
              <h4 class="text-uppercase">${company?.name || 'Company Name'}</h4>
              <p style="width:65%">${company?.address || ''}</p>
              <p><i class="mdi mdi-phone-outline"></i> ${company?.mobile || ''}</p>
              <p><i class="mdi mdi-email-outline"></i> ${company?.email || ''}</p>
              ${company?.website ? `<p><i class="mdi mdi-web"></i> ${company.website}</p>` : ''}
            </div>
            <div class="logo-container">
              ${company?.logo_thumb ? `<img src="${fileServerUrl}${company.logo_thumb}" alt="Logo" class="img-fluid">` : ''}
            </div>
          </div>
          <h4 class="text-center my-4"><strong>BONUS PROCESS REPORT</strong></h4>
          <div class="row mb-3">
            <div class="col-sm-8">
              <p><strong>Year:</strong> ${filters.year || 'All'}</p>
              <p><strong>Month:</strong> ${filters.month ? months.find(m => m.value === filters.month)?.label : 'All'}</p>
            </div>
            <div class="col-sm-4 text-end">
              <p><strong>Total Records:</strong> ${reportData.value.length}</p>
            </div>
          </div>
          <table class="table mt-4">
            <thead>
              <tr>
                <th>SN</th>
                <th>Employee Code</th>
                <th>Employee Name</th>
                <th class="text-end">Bonus Amount</th>
                <th class="text-end">Payable Amount</th>
                <th class="text-center">Payment Status</th>
              </tr>
            </thead>
            <tbody>
              ${reportData.value.map((item, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>${item.employee?.code || '—'}</td>
                  <td><strong>${item.employee?.name || '—'}</strong></td>
                  <td class="text-end">${formatNumber(item.bonus_amount)}</td>
                  <td class="text-end">${formatNumber(item.payable_amount)}</td>
                  <td class="text-center">${formatPaymentStatus(item.payment_status)}</td>
                </tr>
              `).join('')}
              <tr class="totals-row">
                <td colspan="3" class="text-end"><strong>Total:</strong></td>
                <td class="text-end"><strong>${formatNumber(totals.value.bonus_amount)}</strong></td>
                <td class="text-end"><strong>${formatNumber(totals.value.payable_amount)}</strong></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </body>
    </html>
  `)
  printWindow.moveTo(0, 0)
  printWindow.focus()
  await new Promise(resolve => setTimeout(resolve, 500))
  printWindow.print()
  printWindow.close()
}
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>

